/**
 * discord.ts — DISCORD: rich presence and invites (2026-09-24; research §4.18 "a link lands the invitee in the same
 * world"). Two ways in:
 *
 *   A PLAIN LINK — anywhere, Discord included: `…/?join=<token>` (an invite minted by `createInvite`). Discord unfurls
 *     it from the page's Open Graph tags (index.html); the invitee opens it, enters the world, and the token is
 *     redeemed (`redeemInvite`) which stands them beside the inviter. A web page cannot set a Discord user's rich
 *     presence — that is the desktop client's local RPC socket, out of a browser's reach.
 *   A DISCORD ACTIVITY — when Cosmos runs inside Discord (an Activity iframe: the launch URL carries `frame_id` and
 *     `instance_id`) and VITE_DISCORD_CLIENT_ID is set, the Embedded App SDK is loaded (lazily — never on the web):
 *       · `commands.shareLink({ message, custom_id: <token> })` shares the invite; a friend who launches the Activity
 *         from it arrives with `?custom_id=<token>` — redeemed like `?join=`;
 *       · `commands.openInviteDialog()` — Discord's own "invite to this Activity" (no scope needed);
 *       · `commands.setActivity({ activity })` — rich presence (details, state, party size, elapsed time), which needs
 *         the `rpc.activities.write` scope: `authorize` returns a code the SERVER must exchange for an access token
 *         (it holds the client secret) — VITE_DISCORD_TOKEN_URL (default the dev server's `/__atlas/discord-token`,
 *         which needs DISCORD_CLIENT_SECRET in its environment). Without that exchange presence is skipped; the
 *         invites still work.
 * Sources: the Embedded App SDK reference (docs.discord.com/developers/developer-tools/embedded-app-sdk).
 */

type Env = Record<string, string | undefined>;
const env = (): Env => ((import.meta as unknown as { env?: Env }).env ?? {});

export function discordClientId(): string { return env().VITE_DISCORD_CLIENT_ID ?? ''; }
export function discordTokenUrl(): string { return env().VITE_DISCORD_TOKEN_URL ?? '/__atlas/discord-token'; }

/** Is this page running as a Discord Activity? (Discord adds these to the Activity's launch URL.) */
export function inDiscordActivity(search = typeof location !== 'undefined' ? location.search : ''): boolean {
  const q = new URLSearchParams(search);
  return q.has('frame_id') && q.has('instance_id');
}

/** An invite token the page was launched with (`?join=` on the web, `?custom_id=` from a Discord share). */
export function launchJoinToken(search = typeof location !== 'undefined' ? location.search : ''): string | null {
  const q = new URLSearchParams(search);
  const t = (q.get('join') ?? q.get('custom_id') ?? '').trim().toLowerCase();
  return /^[a-z2-9]{8,32}$/.test(t) ? t : null;
}

/** The share link for an invite token, on this page's own origin and path. */
export function inviteUrl(token: string, loc: { origin: string; pathname: string } = location): string {
  return `${loc.origin}${loc.pathname}?join=${encodeURIComponent(token)}`;
}

// ── the Embedded App SDK (loaded only inside an Activity) ────────────────────
interface Sdk {
  ready(): Promise<void>;
  commands: {
    authorize(a: { client_id: string; response_type: 'code'; state: string; prompt: 'none'; scope: string[] }): Promise<{ code: string }>;
    authenticate(a: { access_token: string }): Promise<unknown>;
    setActivity(a: { activity: Record<string, unknown> }): Promise<unknown>;
    openInviteDialog(): Promise<void>;
    shareLink(a: { message: string; custom_id?: string }): Promise<{ success: boolean }>;
  };
}

let sdkP: Promise<Sdk | null> | null = null;
let presenceOk: boolean | null = null;
const state = { loaded: false, ready: false, presence: 'off' as 'off' | 'authorizing' | 'on' | 'no-exchange' | 'failed', lastError: '' };

export function discordState(): Record<string, unknown> {
  return { inActivity: inDiscordActivity(), clientId: !!discordClientId(), ...state };
}

export function discordSdk(): Promise<Sdk | null> {
  if (sdkP) return sdkP;
  sdkP = (async () => {
    if (!inDiscordActivity() || !discordClientId()) return null;
    try {
      const m = await import('@discord/embedded-app-sdk');
      state.loaded = true;
      const sdk = new m.DiscordSDK(discordClientId()) as unknown as Sdk;
      await sdk.ready();
      state.ready = true;
      return sdk;
    } catch (e) {
      state.lastError = String((e as Error)?.message ?? e).slice(0, 160);
      return null;
    }
  })();
  return sdkP;
}

/** Authorize + authenticate for rich presence (once). False when no server exchange is available. */
async function presenceAuth(sdk: Sdk): Promise<boolean> {
  if (presenceOk !== null) return presenceOk;
  state.presence = 'authorizing';
  try {
    const { code } = await sdk.commands.authorize({ client_id: discordClientId(), response_type: 'code', state: '', prompt: 'none', scope: ['identify', 'rpc.activities.write'] });
    const res = await fetch(discordTokenUrl(), { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }) });
    if (!res.ok) { state.presence = 'no-exchange'; presenceOk = false; return false; }
    const { access_token } = (await res.json()) as { access_token?: string };
    if (!access_token) { state.presence = 'no-exchange'; presenceOk = false; return false; }
    await sdk.commands.authenticate({ access_token });
    state.presence = 'on';
    presenceOk = true;
  } catch (e) {
    state.presence = 'failed';
    state.lastError = String((e as Error)?.message ?? e).slice(0, 160);
    presenceOk = false;
  }
  return presenceOk;
}

export interface Presence { details: string; state: string; startMs: number; partyId?: string; partySize?: [number, number] }

/** Rich presence while running as an Activity (a no-op on the web). */
export async function setDiscordPresence(p: Presence): Promise<boolean> {
  const sdk = await discordSdk();
  if (!sdk || !(await presenceAuth(sdk))) return false;
  const activity: Record<string, unknown> = {
    type: 0, details: p.details.slice(0, 128), state: p.state.slice(0, 128), timestamps: { start: Math.floor(p.startMs / 1000) },
  };
  if (p.partyId && p.partySize) activity.party = { id: p.partyId, size: p.partySize };
  try { await sdk.commands.setActivity({ activity }); return true; }
  catch (e) { state.lastError = String((e as Error)?.message ?? e).slice(0, 160); return false; }
}

/** Share an invite from inside Discord (a friend who launches from it arrives with the token). */
export async function shareInviteOnDiscord(token: string, message: string): Promise<boolean> {
  const sdk = await discordSdk();
  if (!sdk) return false;
  try { return (await sdk.commands.shareLink({ message: message.slice(0, 200), custom_id: token })).success; }
  catch { return false; }
}

/** Discord's own invite dialog (people join the same Activity instance). */
export async function openDiscordInviteDialog(): Promise<boolean> {
  const sdk = await discordSdk();
  if (!sdk) return false;
  try { await sdk.commands.openInviteDialog(); return true; } catch { return false; }
}
